/**
 * `useLiveDemo` — hook de ciclo de vida del sembrado y el stream en vivo
 * de la Capa Operacional AI-First (modo DEMO).
 *
 * Encapsula la coordinación de {@link ensureLiveDemoSeed} y
 * {@link startLiveDemoStream} para las vistas que muestran el
 * Ticker_Operacional (LiveOperations, AlertCenter, OperationalMap, tarea
 * 5.5): siembra el Event_Store con eventos derivados de las alarmas reales
 * y arranca el simulador de stream mientras la vista está montada,
 * deteniéndolo al desmontarse. Es seguro usarlo en varias vistas a la vez:
 * la siembra es idempotente y el stream es un singleton compartido.
 *
 * Expone `listo` para que el consumidor difiera la suscripción del Ticker
 * hasta que el backlog esté sembrado (evita un primer render sin eventos).
 */
import { useEffect, useState } from "react";

import { ensureLiveDemoSeed, startLiveDemoStream } from "./liveDemo";

/** Contador de consumidores activos para compartir un único stream. */
let consumidores = 0;
let detenerStream: (() => void) | null = null;

/** Estado expuesto por {@link useLiveDemo}. */
export interface UseLiveDemoState {
  /** True cuando el Event_Store ya está sembrado y el Hub hidratado. */
  listo: boolean;
}

/**
 * Siembra el Event_Store y mantiene vivo el simulador de stream mientras
 * el componente esté montado. Devuelve `listo=true` tras completar la
 * siembra.
 */
export function useLiveDemo(): UseLiveDemoState {
  const [listo, setListo] = useState(false);

  useEffect(() => {
    let cancelado = false;

    consumidores += 1;
    if (consumidores === 1) {
      detenerStream = startLiveDemoStream();
    }

    void ensureLiveDemoSeed()
      .then(() => {
        if (!cancelado) setListo(true);
      })
      .catch(() => {
        // Degradación elegante: si la siembra falla, el Ticker mostrará
        // "sin eventos" en lugar de romper la vista.
        if (!cancelado) setListo(true);
      });

    return () => {
      cancelado = true;
      consumidores -= 1;
      if (consumidores <= 0) {
        consumidores = 0;
        detenerStream?.();
        detenerStream = null;
      }
    };
  }, []);

  return { listo };
}
